import { getResourceType } from '@academy/utils';
import type { ICourse, ICurriculumList, ILesson } from '@academy/graphql/types';

const calculateCourseProgress = (curriculumList: ICurriculumList) => {
  const curriculumListValues = Object.values(curriculumList);
  const quiz = curriculumListValues.find(lesson => lesson.icon === 'QUIZ');
  const curriculumListBeforeQuiz = quiz ? curriculumListValues.slice(0, curriculumListValues.indexOf(quiz) + 1) : curriculumListValues;
  return curriculumListBeforeQuiz.length && curriculumListBeforeQuiz?.filter(lesson => lesson.isCompleted).length / curriculumListBeforeQuiz?.length;
};

const getLevelFromTags = ({ course }: { course: ICourse; }) => {
  const levelTag = course?.tags?.find(tag => tag.startsWith('level:'));
  return levelTag?.split(':')[1];
};

const getFirstLessonLink = (
  { lessons, path }: { lessons: ILesson[]; path: string; },
) => {
  const firstLesson = lessons[0];
  return `${path}/${firstLesson?.pathId}`;
};

const getFirstUncompletedLink = ({ curriculumList, lessons, path }: {
  curriculumList: ICurriculumList;
  lessons: ILesson[];
  path: string;
}) => {
  const firstIncompleteLessonId = Object.entries(curriculumList).find(([_lessonId, lessonDetails]) => !lessonDetails.isCompleted)?.[0];
  const firstIncompleteLesson = lessons.find(({ thirdPartyId }) => thirdPartyId === firstIncompleteLessonId);
  return firstIncompleteLessonId && firstIncompleteLesson ?
    `${path}/${firstIncompleteLesson.pathId}` :
    getFirstLessonLink({ lessons, path });
};

export const getCourseMetaContent = ({
  curriculumList, lessons, path, course, isUserLoggedIn,
}: {
  curriculumList: ICurriculumList;
  lessons: ILesson[];
  path: string;
  course: ICourse;
  isUserLoggedIn: boolean;
}) => {
  const courseProgress = calculateCourseProgress(curriculumList);
  const level = getLevelFromTags({ course });
  const firstIncompleteLessonLink = getFirstUncompletedLink({ lessons, path, curriculumList });
  const firstLessonLink = getFirstLessonLink({ lessons, path });
  const nextLessonLink = courseProgress > 0 ? firstIncompleteLessonLink : firstLessonLink;
  const ctaLink = isUserLoggedIn ?
    nextLessonLink :
    `${import.meta.env.PUBLIC_IFRAME_SOURCE}/auth/login?next=${encodeURIComponent(path)}`;
  const ctaText = courseProgress > 0 ? 'Continue' : 'Get started';

  return {
    courseProgress: Number.isNaN(courseProgress) ? 0 : courseProgress,
    level,
    firstLessonLink,
    nextLessonLink,
    firstIncompleteLessonLink,
    ctaLink,
    ctaText,
    resourceType: getResourceType(course.tags),
  };
};
